// Direction-aware horizontal scrolling. Under `direction: rtl` modern browsers (Chromium 85+,
// Firefox, Safari) report `scrollLeft` from 0 at the inline-start (right) edge down to
// −(scrollWidth − clientWidth). These helpers work in "distance from the inline-start edge"
// (always ≥ 0) so callers can use the same page/offset math in LTR and RTL.

/** The subset of an element the helpers touch — lets tests pass a plain fake object. */
export type InlineScrollElement = Pick<HTMLElement, 'scrollLeft'> & {
  scrollTo?: (options: ScrollToOptions) => void;
  scrollBy?: (options: ScrollToOptions) => void;
};

const isRtl = (el: InlineScrollElement): boolean => getComputedStyle(el as unknown as Element).direction === 'rtl';

/** +1 when the inline-end direction is physical right (LTR), −1 when it is left (RTL). */
export const inlineDirectionSign = (el: InlineScrollElement): 1 | -1 => (isRtl(el) ? -1 : 1);

/** Distance scrolled away from the inline-start edge (≥ 0) in either direction. */
export const scrollStartOffset = (el: InlineScrollElement): number =>
  Math.max(0, el.scrollLeft * inlineDirectionSign(el));

/** Scrolls so the element sits `offset` px from its inline-start edge. */
export const scrollToStartOffset = (el: InlineScrollElement, offset: number, behavior?: ScrollBehavior): void => {
  // `|| 0` normalises −0 so LTR and RTL both land on a plain 0 at the start edge.
  const left = offset * inlineDirectionSign(el) || 0;
  if (behavior && el.scrollTo) {
    el.scrollTo({ left, behavior });
    return;
  }
  el.scrollLeft = left;
};

/** Scrolls by `delta` px toward the inline end (negative `delta` goes toward the start). */
export const scrollByInline = (el: InlineScrollElement, delta: number, behavior?: ScrollBehavior): void => {
  const left = delta * inlineDirectionSign(el) || 0;
  if (el.scrollBy) {
    el.scrollBy({ left, behavior });
    return;
  }
  el.scrollLeft += left;
};
